'use client';

import { useId, type ReactNode } from 'react';

import type { OrderInfo } from '@eliadmin/core/src/api/claims/claims-api';
import {
  ICON_STROKE,
  TEXT_ACTION,
} from '@eliadmin/core/src/components/dlz/account/orders/order-parts';
import type { OrderStage } from '@eliadmin/core/src/components/dlz/account/orders/order-stage';
import { ORDERS_COPY } from '@eliadmin/core/src/components/dlz/account/orders/orders-copy';
import { formatPhone } from '@eliadmin/core/src/components/dlz/auth/register/register-values';
import { Icon, type IconName } from '@eliadmin/core/src/components/dlz/ui/icon';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { formatPostcode } from '@eliadmin/core/src/components/dlz/auth/register/register-values';
import { cn } from '@eliadmin/core/src/utils/style-utils';

type Country = OrderInfo['country'] | null;

/** „974 01 Banská Bystrica“, the country only when it is not the store's own. */
function placeLine(code: string | null, city: string | null, country: Country) {
  const place = [formatPostcode(code, country?.id), city].filter(Boolean).join(' ');
  return [place, country && !country.is_default ? country.name : ''].filter(Boolean).join(', ');
}

function streetLine(street: string | null, number: string | null) {
  return [street, number].filter(Boolean).join(' ');
}

/** `/orders/info` sends the phone on some backends; the core type does not declare it. */
function orderPhone(order: OrderInfo) {
  const { phone } = order as OrderInfo & { phone?: unknown };
  return typeof phone === 'string' && phone.trim() ? formatPhone(phone) : '';
}

/**
 * Figma „Block · …“ (60324:20931; phones 60459:92306): 18 px duotone icon in text/brand and
 * DLZ/Body/S Medium title (narrow column: 24 px icon, DLZ/Heading/H5), DLZ/Body/S lines, 8 px
 * apart.
 */
function InfoCard({
  icon,
  title,
  lines,
  children,
}: {
  icon: IconName;
  title: string;
  lines: string[];
  children?: ReactNode;
}) {
  const titleId = useId();

  return (
    <section
      aria-labelledby={titleId}
      className="flex min-w-0 flex-col gap-2 rounded-[18px] border border-[#E2E4E7] bg-white p-4 md:p-6"
    >
      <h2
        id={titleId}
        className="dlz-h5 @[30rem]:dlz-body-s-medium flex items-center gap-2 text-[#0F1012]"
      >
        <Icon
          name={icon}
          size={24}
          className={cn('text-[#D7191F] @[30rem]:size-[18px]', ICON_STROKE)}
        />
        {title}
      </h2>
      <div className="dlz-body-s flex flex-col gap-2 text-[#0F1012] [overflow-wrap:anywhere]">
        {lines.filter(Boolean).map((line, index) => (
          <p key={index}>{line}</p>
        ))}
        {children}
      </div>
    </section>
  );
}

/**
 * Figma „Info“ (60324:20911 + 60317:25466): contact, delivery, payment and billing address, 2 × 2
 * from a 40rem column. The design's „Visa •••• 4821“ is left out (the API has no card details); the
 * contact card uses the person icon of the checkout's „Kontaktné údaje“ (the design repeats the
 * payment card's icon there).
 */
export function OrderInfoCards({ info, stage }: { info: OrderInfo; stage: OrderStage }) {
  const { t } = useTranslations();
  const name = [info.name, info.surname].filter(Boolean).join(' ');
  // `/orders/info` has no recipient for another delivery address, so no name is shown for it.
  const delivery = info.different_delivery_address
    ? {
        recipient: '',
        street: streetLine(info.address2, info.street_number2),
        place: placeLine(info.postcode2, info.city2, info.country2),
      }
    : {
        recipient: name,
        street: streetLine(info.address, info.street_number),
        place: placeLine(info.postcode, info.city, info.country),
      };
  const document = info.documents[0];
  const payable = !info.paid && !!info.payment_url && stage !== 'cancelled';

  return (
    <div className="grid gap-4 @[30rem]:gap-y-6 @[40rem]:grid-cols-2">
      <InfoCard icon="user" title={t('text360')} lines={[name, info.email, orderPhone(info)]} />
      <InfoCard
        icon="truck"
        title={t('text3497')}
        lines={[
          info.shipment?.name ?? '',
          delivery.recipient,
          [delivery.street, delivery.place].filter(Boolean).join(', '),
        ]}
      />
      <InfoCard
        icon="credit-card"
        title={t('text158')}
        lines={[
          info.payment_type?.name ?? '',
          document ? ORDERS_COPY.documentNumber(document.name, document.number) : '',
        ]}
      >
        {payable ? (
          <a
            href={info.payment_url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(TEXT_ACTION, 'w-fit')}
          >
            {t('text207')}
          </a>
        ) : null}
      </InfoCard>
      <InfoCard
        icon="notebook"
        title={t('text45')}
        lines={[
          name,
          streetLine(info.address, info.street_number),
          placeLine(info.postcode, info.city, info.country),
        ]}
      />
    </div>
  );
}
